import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import CustomSlider from 'components/Sliders/CustomSlider';
import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsCheck } from 'react-icons/bs';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { GiSpeaker, GiSpeakerOff } from 'react-icons/gi';
import { useDispatch, useSelector } from 'react-redux';
import { setAlarmVolume } from '../../slices/alarmsSlice';
import styles from './assets/AlarmsHeader.module.css';

interface AlarmsHeaderProps {
  title: string;
  pageIndex: number;
  maxPageIndex: number;
  headerFields: string[];
  onPageChanged: (page: number) => void;
  onLimitChanged: (limit: number) => void;
  /** Current page size. Defaults to the first-page default of 25. */
  limit?: number;
  onSearchChanged: (search: string) => void;
  silenceControlsEnabled?: boolean;
  allSelected?: boolean;
  onSelectAll?: () => void;
  onAcknowledge?: () => void;
  onSilence?: () => void;
  onUnsilence?: () => void;
}

const LIMIT_OPTIONS = [10, 25, 50, 100].map((value) => ({ value, label: String(value) }));

const LIMIT_SELECT_STYLES = {
  container: (base: Record<string, unknown>) => ({
    ...base,
    width: 80,
    minWidth: 60,
    maxWidth: 100,
  }),
};

const AlarmsHeader: React.FC<AlarmsHeaderProps> = ({
  title,
  pageIndex,
  maxPageIndex,
  headerFields,
  onPageChanged,
  onLimitChanged,
  limit = 25,
  onSearchChanged,
  silenceControlsEnabled = false,
  allSelected = false,
  onSelectAll = () => {},
  onAcknowledge = () => {},
  onSilence = () => {},
  onUnsilence = () => {},
}) => {
  const setPageIndex = (newPageIndex: number) => {
    const boundedIndex = Math.max(0, Math.min(maxPageIndex, newPageIndex));
    onPageChanged(boundedIndex);
  };

  const setLimit = (newLimitValue: number) => {
    onLimitChanged(newLimitValue);
  };

  const dispatch = useDispatch();
  // Redux state is float 0.3-1.0, slider is 30-100
  const volume = useSelector((state: any) => state.alarmVolume?.alarmVolume ?? 1.0);
  const handleVolumeChange = (val: number) => {
    let v = val;
    if (v < 30) v = 30;
    if (v > 100) v = 100;
    dispatch(setAlarmVolume(v / 100));
  };

  return (
    <div className="alarm-header d-flex flex-column py-3 user-select-none text-theme w-100">
      <div className="flex-shrink-0">
        <div className="w-100 g-3 align-items-start row">
          <div className="d-flex flex-column align-items-center align-items-lg-start gap-2 col-12 col-lg-8">
            <div className="fw-bold fs-4 text-center text-lg-start w-100">{title}</div>
            <div className="d-flex flex-column gap-3 justify-content-center justify-content-lg-start">
              {silenceControlsEnabled && (
                <>
                  <div className={styles.alarmActions}>
                    <div className={styles.alarmAction}>
                      <Button
                        aria-label={allSelected ? 'Deselect all alarms' : 'Select all alarms'}
                        variant={allSelected ? 'primary' : 'transparent'}
                        className={`rounded-circle d-flex align-items-center justify-content-center p-0 border fs-4 ${
                          allSelected
                            ? 'border-theme-strong bg-primary'
                            : 'border-theme-strong bg-transparent text-theme'
                        }`}
                        style={{ width: 34, height: 34, minWidth: 34, minHeight: 34 }}
                        onClick={onSelectAll}
                      >
                        {allSelected && <BsCheck size={20} />}
                      </Button>
                      Select All
                    </div>
                    <div className={styles.alarmAction}>
                      <Button
                        aria-label="Acknowledge selected alarms"
                        variant="primary"
                        className="rounded-circle d-flex align-items-center justify-content-center p-0 border border-theme-strong bg-primary fs-4"
                        style={{ width: 34, height: 34, minWidth: 34, minHeight: 34 }}
                        onClick={onAcknowledge}
                      >
                        <BsCheck size={20} />
                      </Button>
                      Acknowledge
                    </div>
                    <div className={styles.alarmAction}>
                      <Button
                        aria-label="Silence selected alarms"
                        variant="primary"
                        className="rounded-circle d-flex align-items-center justify-content-center p-0 border border-theme-strong bg-primary fs-4"
                        style={{ width: 34, height: 34, minWidth: 34, minHeight: 34 }}
                        onClick={onSilence}
                      >
                        <GiSpeakerOff size={20} />
                      </Button>
                      Silence
                    </div>
                    <div className={styles.alarmAction}>
                      <Button
                        aria-label="Unsilence selected alarms"
                        variant="primary"
                        className="rounded-circle d-flex align-items-center justify-content-center p-0 border border-theme-strong bg-primary fs-4"
                        style={{ width: 34, height: 34, minWidth: 34, minHeight: 34 }}
                        onClick={onUnsilence}
                      >
                        <GiSpeaker size={20} />
                      </Button>
                      Unsilence
                    </div>
                  </div>
                  <div className={styles.volumeControl}>
                    <span>Volume</span>
                    <div className={styles.volumeSlider}>
                      <CustomSlider
                        min={30}
                        max={100}
                        step={1}
                        value={Math.round(volume * 100)}
                        onChange={handleVolumeChange}
                        tooltipFormatter={(v) => `${v}%`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </>
              )}
              {maxPageIndex >= 1 && (
                <div className="d-flex align-items-center gap-2 justify-content-center justify-content-lg-start">
                  <Button
                    variant="transparent"
                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center mx-1 border border-theme-strong text-theme"
                    onClick={() => setPageIndex(pageIndex - 1)}
                    disabled={pageIndex === 0}
                  >
                    <FaChevronLeft
                      className={pageIndex === 0 ? 'text-theme-muted' : ''}
                      size={20}
                    />
                  </Button>
                  <div>{`Page ${pageIndex + 1} of ${maxPageIndex + 1}`}</div>
                  <Button
                    variant="transparent"
                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center mx-1 border border-theme-strong text-theme"
                    onClick={() => setPageIndex(pageIndex + 1)}
                    disabled={pageIndex >= maxPageIndex}
                  >
                    <FaChevronRight
                      className={pageIndex >= maxPageIndex ? 'text-theme-muted' : ''}
                      size={20}
                    />
                  </Button>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <div>Limit</div>
                <BaseSelect
                  isSearchable={false}
                  classNames={{
                    control: () => 'border-theme-strong text-theme bg-surface-inset',
                  }}
                  styles={LIMIT_SELECT_STYLES}
                  selectProps={{
                    'aria-label': 'Limit',
                    options: LIMIT_OPTIONS,
                    value: findSelectOption(LIMIT_OPTIONS, limit),
                    onChange: (option: { value: number } | null) => {
                      if (option) setLimit(option.value);
                    },
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center justify-content-lg-end col-12 col-lg-4">
            <Form.Control
              className={[
                styles.search,
                'alarm-header-search rounded-3 border border-theme-strong bg-surface-inset text-theme',
              ].join(' ')}
              type="search"
              placeholder="Search"
              onChange={(e) => onSearchChanged(e.target.value.replace('CW_', ''))}
            />
          </div>
        </div>
        <div className="gx-0 mt-2 row">
          {headerFields?.map((header) => (
            <div
              key={header}
              className="d-flex flex-column w-100 col border-bottom border-secondary pb-1 mb-2"
            >
              {header}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlarmsHeader;
